import { Component } from '@theme/component';
import { getIntersectionRoot } from '@theme/scroll-container';

/**
 * Collection filter nav.
 *
 * Handles the three things the CSS can't do on its own:
 *
 * 1. Pinned state. A sentinel is inserted just before the section wrapper and
 *    watched with an IntersectionObserver whose root is the actual scroll
 *    container (`.page-wrapper` above 990px, the document below it — see
 *    assets/scroll-container.js). When the sentinel crosses the sticky offset,
 *    `data-stuck` flips and the bar compacts.
 *
 * 2. Height lock. The bar shrinks when pinned, which would pull the page
 *    content up. The component stores its resting height in `--cfn-rest-height`
 *    and keeps it as a min-height, so the layout below never moves.
 *
 * 3. Scroll affordances. `data-overflow` on the list drives the edge fades, and
 *    the active chip is centred whenever the list becomes scrollable.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} bar
 * @property {HTMLElement} list
 *
 * @extends {Component<Refs>}
 */
class CollectionFilterNav extends Component {
  requiredRefs = ['bar', 'list'];

  /** @type {HTMLElement | null} */
  #section = null;

  /** @type {HTMLElement | null} */
  #sentinel = null;

  /** @type {IntersectionObserver | null} */
  #stickyObserver = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number | null} */
  #scrollRaf = null;

  /** @type {number | null} */
  #resizeTimeout = null;

  connectedCallback() {
    super.connectedCallback();

    this.#section = this.closest('.shopify-section');

    this.#resolveDuplicateTransitionName();
    this.#createSentinel();
    this.#lockRestingHeight();
    this.#connectStickyObserver();
    this.#watchSizes();

    this.refs.list.addEventListener('scroll', this.#handleListScroll, { passive: true });
    window.addEventListener('resize', this.#handleResize);

    this.#updateOverflowState();
    this.#centerActiveItem();

    // Webfonts can change the label height after the first measurement.
    document.fonts?.ready.then(() => {
      if (!this.isConnected) return;
      this.#lockRestingHeight();
      this.#updateOverflowState();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.list?.removeEventListener('scroll', this.#handleListScroll);
    window.removeEventListener('resize', this.#handleResize);

    this.#stickyObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#sentinel?.remove();

    if (this.#scrollRaf) cancelAnimationFrame(this.#scrollRaf);
    if (this.#resizeTimeout) clearTimeout(this.#resizeTimeout);

    this.#stickyObserver = null;
    this.#resizeObserver = null;
    this.#sentinel = null;
  }

  /**
   * Two navs on one page would declare the same view-transition-name, which
   * makes the browser abort the whole transition. First one wins.
   */
  #resolveDuplicateTransitionName() {
    if (this.dataset.viewTransition !== 'true') return;

    const navs = document.querySelectorAll('collection-filter-nav[data-view-transition="true"]');
    if (navs.length < 2) return;

    for (const [index, nav] of navs.entries()) {
      if (index > 0 && nav instanceof HTMLElement) nav.style.setProperty('view-transition-name', 'none');
    }
  }

  /**
   * A zero-height marker sitting where the bar would be if it never stuck.
   * It has to live outside the sticky wrapper, otherwise it travels with it.
   */
  #createSentinel() {
    if (!this.#section?.parentElement) return;

    this.#sentinel = document.createElement('div');
    this.#sentinel.setAttribute('aria-hidden', 'true');
    this.#sentinel.style.cssText = 'height:0;width:100%;pointer-events:none;';
    this.#section.before(this.#sentinel);
  }

  /**
   * Measures the bar at its full size and pins that height on the component.
   */
  #lockRestingHeight() {
    if (this.dataset.compact !== 'true') return;

    const wasStuck = this.dataset.stuck === 'true';
    if (wasStuck) this.dataset.stuck = 'false';

    const height = this.refs.bar.offsetHeight;

    if (wasStuck) this.dataset.stuck = 'true';
    if (height > 0) this.style.setProperty('--cfn-rest-height', `${height}px`);
  }

  /**
   * (Re)builds the observer. The root margin has to match the sticky offset,
   * which depends on the current header height, so this reruns whenever the
   * header resizes or the breakpoint changes.
   */
  #connectStickyObserver() {
    this.#stickyObserver?.disconnect();
    this.#stickyObserver = null;

    if (!this.#section || !this.#sentinel) return;

    const styles = getComputedStyle(this.#section);
    if (styles.position !== 'sticky') {
      this.#setStuck(false);
      return;
    }

    const offset = Math.round(parseFloat(styles.top) || 0);

    this.#stickyObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;

        const rootTop = entry.rootBounds?.top ?? 0;
        this.#setStuck(!entry.isIntersecting && entry.boundingClientRect.top <= rootTop);
      },
      {
        root: getIntersectionRoot(),
        rootMargin: `${-offset}px 0px 0px 0px`,
        threshold: 0,
      }
    );

    this.#stickyObserver.observe(this.#sentinel);
  }

  /**
   * @param {boolean} stuck
   */
  #setStuck(stuck) {
    const next = stuck ? 'true' : 'false';
    if (this.dataset.stuck === next) return;

    this.dataset.stuck = next;

    // The list can become scrollable as it collapses, so re-evaluate once the
    // shrink transition has settled.
    setTimeout(() => {
      this.#updateOverflowState();
      this.#centerActiveItem();
    }, 250);
  }

  #watchSizes() {
    const header = document.querySelector('#header-component');
    if (!header) return;

    this.#resizeObserver = new ResizeObserver(() => this.#connectStickyObserver());
    this.#resizeObserver.observe(header);
  }

  #handleResize = () => {
    if (this.#resizeTimeout) clearTimeout(this.#resizeTimeout);

    this.#resizeTimeout = setTimeout(() => {
      this.#lockRestingHeight();
      this.#connectStickyObserver();
      this.#updateOverflowState();
    }, 150);
  };

  #handleListScroll = () => {
    if (this.#scrollRaf) return;

    this.#scrollRaf = requestAnimationFrame(() => {
      this.#scrollRaf = null;
      this.#updateOverflowState();
    });
  };

  /**
   * Tells the CSS which edge, if any, still hides content.
   */
  #updateOverflowState() {
    const list = this.refs.list;
    if (!list) return;

    const max = list.scrollWidth - list.clientWidth;

    if (max <= 1) {
      list.dataset.overflow = 'none';
    } else if (list.scrollLeft <= 1) {
      list.dataset.overflow = 'end';
    } else if (list.scrollLeft >= max - 1) {
      list.dataset.overflow = 'start';
    } else {
      list.dataset.overflow = 'both';
    }
  }

  /**
   * Brings the current collection into view without touching page scroll.
   */
  #centerActiveItem() {
    const list = this.refs.list;
    const active = list?.querySelector('[aria-current="page"]');

    if (!list || !(active instanceof HTMLElement)) return;
    if (list.scrollWidth <= list.clientWidth + 1) return;

    const target = active.offsetLeft - (list.clientWidth - active.offsetWidth) / 2;
    list.scrollTo({ left: Math.max(0, target), behavior: 'instant' });
  }
}

if (!customElements.get('collection-filter-nav')) {
  customElements.define('collection-filter-nav', CollectionFilterNav);
}